import { useEffect, useState, useCallback } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  KeyIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Header } from '@/components/Layout';
import { Modal, ConfirmDialog, PageLoader, EmptyState } from '@/components/common';
import { apiKeysApi, organizationsApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { PERMISSIONS } from '@/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ApiKey {
  _id: string;
  name: string;
  key_prefix: string;
  permissions: string[];
  rate_limit: number;
  active: boolean;
  last_used_at?: string;
  expires_at?: string;
  created_at: string;
}

interface CreateForm {
  name: string;
  organization_id: string;
  permissions: string[];
  rate_limit: number;
  expires_at: string;
}

interface EditForm {
  name: string;
  permissions: string[];
  rate_limit: number;
  active: boolean;
}

// ---------------------------------------------------------------------------
// Permission groups — las API keys solo pueden leer contenido de su organización
// (VIDA-318); el backend rechaza cualquier otro permiso.
// ---------------------------------------------------------------------------

const permissionGroups = [
  {
    name: 'Contenido',
    permissions: [
      { key: PERMISSIONS.CONTENT_READ, label: 'Ver (legado)' },
      { key: PERMISSIONS.CMS_READ, label: 'Ver' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatRelative(dateStr?: string): string {
  if (!dateStr) return 'Nunca';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Ahora mismo';
  if (diffMins < 60) return `Hace ${diffMins} min`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays < 7) return `Hace ${diffDays}d`;
  return formatDate(dateStr);
}

function isExpired(dateStr?: string): boolean {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
}

// ---------------------------------------------------------------------------
// Sub-component: Revealed key dialog shown once after creation
// ---------------------------------------------------------------------------

interface RevealKeyDialogProps {
  open: boolean;
  apiKey: string;
  onClose: () => void;
}

function RevealKeyDialog({ open, apiKey, onClose }: RevealKeyDialogProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      toast.success('Clave copiada al portapapeles');
      setTimeout(() => setCopied(false), 3000);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-xl bg-white shadow-xl transition-all">
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-full bg-green-100">
                      <CheckCircleIcon className="w-6 h-6 text-green-600" />
                    </div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      API Key creada correctamente
                    </Dialog.Title>
                  </div>

                  {/* Warning */}
                  <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                    <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      <span className="font-semibold">Guarda esta clave ahora.</span> No podrás verla de nuevo una vez que cierres este dialogo.
                    </p>
                  </div>

                  {/* Key display */}
                  <div className="mb-5">
                    <label className="label">Tu API Key</label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 block px-3 py-2.5 bg-gray-900 text-green-400 rounded-lg text-sm font-mono break-all border border-gray-700 select-all">
                        {apiKey}
                      </code>
                      <button
                        type="button"
                        onClick={handleCopy}
                        className="flex-shrink-0 p-2.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-vida-600 transition-colors"
                        title="Copiar al portapapeles"
                        aria-label="Copiar clave al portapapeles"
                      >
                        {copied ? (
                          <ClipboardDocumentCheckIcon className="w-5 h-5 text-green-600" />
                        ) : (
                          <ClipboardDocumentIcon className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={onClose}
                    >
                      Entendido, he guardado la clave
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: Permission multi-select checkboxes
// ---------------------------------------------------------------------------

interface PermissionsPickerProps {
  selected: string[];
  onChange: (permissions: string[]) => void;
}

function PermissionsPicker({ selected, onChange }: PermissionsPickerProps) {
  const toggle = (key: string) => {
    onChange(
      selected.includes(key)
        ? selected.filter((p) => p !== key)
        : [...selected, key]
    );
  };

  return (
    <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
      {permissionGroups.map((group) => (
        <div key={group.name} className="border border-gray-200 rounded-lg p-3">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
            {group.name}
          </p>
          <div className="flex flex-wrap gap-2">
            {group.permissions.map((perm) => {
              const active = selected.includes(perm.key);
              return (
                <label
                  key={perm.key}
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs cursor-pointer transition-colors border ${
                    active
                      ? 'bg-vida-100 text-vida-700 border-vida-300'
                      : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={active}
                    onChange={() => toggle(perm.key)}
                    aria-label={`${group.name}: ${perm.label}`}
                  />
                  {perm.label}
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const EMPTY_CREATE_FORM: CreateForm = {
  name: '',
  organization_id: '',
  permissions: [],
  rate_limit: 100,
  expires_at: '',
};

const EMPTY_EDIT_FORM: EditForm = {
  name: '',
  permissions: [],
  rate_limit: 100,
  active: true,
};

export default function ApiKeysPage() {
  const [loading, setLoading] = useState(true);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRevealDialog, setShowRevealDialog] = useState(false);

  // Selection
  const [selectedKey, setSelectedKey] = useState<ApiKey | null>(null);
  const [revealedKey, setRevealedKey] = useState('');

  // Forms
  const [createForm, setCreateForm] = useState<CreateForm>(EMPTY_CREATE_FORM);
  const [editForm, setEditForm] = useState<EditForm>(EMPTY_EDIT_FORM);

  // Loading states
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Las claves solo funcionan ligadas a una organización (VIDA-318), y borrarlas
  // es solo de super_admin: el Equipo técnico VIDA las desactiva (VIDA-347).
  const isSuperAdmin = useAuthStore((state) => state.hasPermission('*'));
  const [orgs, setOrgs] = useState<Array<{ _id: string; name: string }>>([]);

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  const loadApiKeys = useCallback(async (page = 1) => {
    try {
      const response = await apiKeysApi.list(page, 20);
      const { apiKeys: keys, total, pages } = response.data;
      setApiKeys(keys || []);
      setPagination({ page, pages: pages || 1, total: total || 0 });
    } catch {
      toast.error('Error al cargar las API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApiKeys();
  }, [loadApiKeys]);

  useEffect(() => {
    organizationsApi
      .list(1, 500)
      .then((res) => setOrgs(res.data.organizations || []))
      .catch(() => toast.error('Error al cargar las organizaciones'));
  }, []);

  // ---------------------------------------------------------------------------
  // Create
  // ---------------------------------------------------------------------------

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!createForm.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!createForm.organization_id) {
      toast.error('Elige la organización de la clave');
      return;
    }

    setCreateLoading(true);
    try {
      const payload: {
        name: string;
        organization_id: string;
        permissions: string[];
        rate_limit: number;
        expires_at?: string;
      } = {
        name: createForm.name.trim(),
        organization_id: createForm.organization_id,
        permissions: createForm.permissions,
        rate_limit: createForm.rate_limit,
      };

      if (createForm.expires_at) {
        payload.expires_at = new Date(createForm.expires_at).toISOString();
      }

      const response = await apiKeysApi.create(payload);
      const fullKey: string = response.data.key || response.data.api_key || '';

      setShowCreateModal(false);
      setCreateForm(EMPTY_CREATE_FORM);
      await loadApiKeys();

      if (fullKey) {
        setRevealedKey(fullKey);
        setShowRevealDialog(true);
      } else {
        toast.success('API Key creada');
      }
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Error al crear la API Key';
      toast.error(message);
    } finally {
      setCreateLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Edit
  // ---------------------------------------------------------------------------

  const openEditModal = (key: ApiKey) => {
    setSelectedKey(key);
    setEditForm({
      name: key.name,
      permissions: key.permissions.filter((p) =>
        permissionGroups.some((g) => g.permissions.some((gp) => gp.key === p))
      ),
      rate_limit: key.rate_limit,
      active: key.active,
    });
    setShowEditModal(true);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedKey || !editForm.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setEditLoading(true);
    try {
      await apiKeysApi.update(selectedKey._id, {
        name: editForm.name.trim(),
        permissions: editForm.permissions,
        rate_limit: editForm.rate_limit,
        active: editForm.active,
      });
      toast.success('API Key actualizada');
      setShowEditModal(false);
      setSelectedKey(null);
      await loadApiKeys();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Error al actualizar la API Key';
      toast.error(message);
    } finally {
      setEditLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------

  const openDeleteConfirm = (key: ApiKey) => {
    setSelectedKey(key);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!selectedKey) return;

    setDeleteLoading(true);
    try {
      await apiKeysApi.delete(selectedKey._id);
      toast.success('API Key eliminada');
      setShowDeleteConfirm(false);
      setSelectedKey(null);
      await loadApiKeys();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Error al eliminar la API Key';
      toast.error(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) return <PageLoader />;

  return (
    <div>
      <Header
        title="API Keys"
        subtitle="Gestiona las claves de acceso a la API"
        actions={
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
            aria-label="Crear nueva API key"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Crear API Key
          </button>
        }
      />

      <div className="p-6">
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Las API keys solo permiten leer el contenido de su organización. Las que no están
          asociadas a una organización no funcionan: créalas desde Organizaciones → API keys.
        </div>
        {apiKeys.length === 0 ? (
          <EmptyState
            title="No hay API Keys"
            description="Crea tu primera API Key para acceder a la API programaticamente"
          />
        ) : (
          <div className="card">
            {/* Stats bar */}
            <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-6 text-sm text-gray-500">
              <span>
                <span className="font-medium text-gray-900">{pagination.total}</span> claves en total
              </span>
              <span>
                <span className="font-medium text-green-700">
                  {apiKeys.filter((k) => k.active && !isExpired(k.expires_at)).length}
                </span>{' '}
                activas
              </span>
              <span>
                <span className="font-medium text-red-700">
                  {apiKeys.filter((k) => !k.active || isExpired(k.expires_at)).length}
                </span>{' '}
                inactivas / expiradas
              </span>
            </div>

            {/* Table */}
            <div className="table-container">
              <table className="table" role="table" aria-label="Listado de API Keys">
                <thead>
                  <tr>
                    <th scope="col">Nombre</th>
                    <th scope="col">Prefijo</th>
                    <th scope="col">Permisos</th>
                    <th scope="col">Rate Limit</th>
                    <th scope="col">Estado</th>
                    <th scope="col">Último uso</th>
                    <th scope="col">Expira</th>
                    <th scope="col">Creada</th>
                    <th scope="col">
                      <span className="sr-only">Acciones</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {apiKeys.map((key) => {
                    const expired = isExpired(key.expires_at);
                    const effectivelyActive = key.active && !expired;

                    return (
                      <tr key={key._id}>
                        {/* Name */}
                        <td>
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 rounded-md bg-vida-50">
                              <KeyIcon className="w-4 h-4 text-vida-600" />
                            </div>
                            <span className="font-medium text-gray-900">{key.name}</span>
                          </div>
                        </td>

                        {/* Key prefix */}
                        <td>
                          <code className="px-2 py-1 bg-gray-100 rounded text-xs font-mono text-gray-700">
                            {key.key_prefix
                              ? `vida_${key.key_prefix}...`
                              : '—'}
                          </code>
                        </td>

                        {/* Permissions count */}
                        <td>
                          {key.permissions.length === 0 ? (
                            <span className="badge-gray text-xs">Sin permisos</span>
                          ) : key.permissions.includes('*') ? (
                            <span className="badge-success text-xs">Todos</span>
                          ) : (
                            <span className="badge-info text-xs">
                              {key.permissions.length} permisos
                            </span>
                          )}
                        </td>

                        {/* Rate limit */}
                        <td>
                          <span className="text-gray-700">
                            {key.rate_limit.toLocaleString('es-ES')} req/min
                          </span>
                        </td>

                        {/* Status */}
                        <td>
                          {effectivelyActive ? (
                            <span className="inline-flex items-center gap-1 badge-success">
                              <CheckCircleIcon className="w-3.5 h-3.5" />
                              Activa
                            </span>
                          ) : expired ? (
                            <span className="inline-flex items-center gap-1 badge-warning">
                              <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                              Expirada
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 badge-danger">
                              <XCircleIcon className="w-3.5 h-3.5" />
                              Inactiva
                            </span>
                          )}
                        </td>

                        {/* Last used */}
                        <td>
                          <span className="text-gray-500 text-xs">
                            {formatRelative(key.last_used_at)}
                          </span>
                        </td>

                        {/* Expires */}
                        <td>
                          {key.expires_at ? (
                            <span
                              className={`text-xs ${expired ? 'text-red-600 font-medium' : 'text-gray-500'}`}
                            >
                              {formatDate(key.expires_at)}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">Nunca</span>
                          )}
                        </td>

                        {/* Created */}
                        <td>
                          <span className="text-gray-500 text-xs">
                            {formatDate(key.created_at)}
                          </span>
                        </td>

                        {/* Actions */}
                        <td>
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => openEditModal(key)}
                              className="p-1.5 text-gray-400 hover:text-vida-600 rounded transition-colors"
                              title="Editar"
                              aria-label={`Editar API key ${key.name}`}
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            {isSuperAdmin && (
                              <button
                                onClick={() => openDeleteConfirm(key)}
                                className="p-1.5 text-gray-400 hover:text-red-600 rounded transition-colors"
                                title="Eliminar"
                                aria-label={`Eliminar API key ${key.name}`}
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500">
                <span>Pagina {pagination.page} de {pagination.pages}</span>
                <div className="flex gap-2">
                  <button
                    className="btn-secondary py-1 px-3 text-xs"
                    disabled={pagination.page <= 1}
                    onClick={() => loadApiKeys(pagination.page - 1)}
                    aria-label="Pagina anterior"
                  >
                    Anterior
                  </button>
                  <button
                    className="btn-secondary py-1 px-3 text-xs"
                    disabled={pagination.page >= pagination.pages}
                    onClick={() => loadApiKeys(pagination.page + 1)}
                    aria-label="Pagina siguiente"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Create modal                                                        */}
      {/* ------------------------------------------------------------------ */}
      <Modal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setCreateForm(EMPTY_CREATE_FORM);
        }}
        title="Crear API Key"
        size="lg"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          {/* Name */}
          <div>
            <label htmlFor="create-name" className="label">
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              id="create-name"
              type="text"
              className="input"
              placeholder="Mi integración externa"
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              required
              autoFocus
            />
          </div>

          {/* Organization */}
          <div>
            <label htmlFor="create-org" className="label">
              Organización <span className="text-red-500">*</span>
            </label>
            <select
              id="create-org"
              className="input"
              value={createForm.organization_id}
              onChange={(e) => setCreateForm({ ...createForm, organization_id: e.target.value })}
              required
            >
              <option value="">Elige una organización…</option>
              {orgs.map((o) => (
                <option key={o._id} value={o._id}>
                  {o.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">La clave solo podrá leer el contenido de esta organización.</p>
          </div>

          {/* Permissions */}
          <div>
            <label className="label mb-2">
              Permisos{' '}
              <span className="text-gray-400 font-normal text-xs ml-1">
                ({createForm.permissions.length} seleccionados)
              </span>
            </label>
            <PermissionsPicker
              selected={createForm.permissions}
              onChange={(permissions) => setCreateForm({ ...createForm, permissions })}
            />
          </div>

          {/* Rate limit */}
          <div>
            <label htmlFor="create-rate-limit" className="label">
              Rate Limit (req/min)
            </label>
            <input
              id="create-rate-limit"
              type="number"
              min={1}
              max={10000}
              className="input"
              value={createForm.rate_limit}
              onChange={(e) =>
                setCreateForm({ ...createForm, rate_limit: Number(e.target.value) })
              }
            />
            <p className="mt-1 text-xs text-gray-400">
              Numero maximo de peticiones por minuto desde esta clave.
            </p>
          </div>

          {/* Expiration */}
          <div>
            <label htmlFor="create-expires" className="label">
              Fecha de expiración{' '}
              <span className="text-gray-400 font-normal text-xs ml-1">(opcional)</span>
            </label>
            <input
              id="create-expires"
              type="date"
              className="input"
              min={new Date().toISOString().split('T')[0]}
              value={createForm.expires_at}
              onChange={(e) => setCreateForm({ ...createForm, expires_at: e.target.value })}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowCreateModal(false);
                setCreateForm(EMPTY_CREATE_FORM);
              }}
              disabled={createLoading}
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={createLoading}>
              {createLoading ? 'Creando...' : 'Crear API Key'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ------------------------------------------------------------------ */}
      {/* Edit modal                                                          */}
      {/* ------------------------------------------------------------------ */}
      <Modal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedKey(null);
        }}
        title="Editar API Key"
        size="lg"
      >
        <form onSubmit={handleEdit} className="space-y-4">
          {/* Name */}
          <div>
            <label htmlFor="edit-name" className="label">
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              id="edit-name"
              type="text"
              className="input"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              required
              autoFocus
            />
          </div>

          {/* Permissions */}
          <div>
            <label className="label mb-2">
              Permisos{' '}
              <span className="text-gray-400 font-normal text-xs ml-1">
                ({editForm.permissions.length} seleccionados)
              </span>
            </label>
            <PermissionsPicker
              selected={editForm.permissions}
              onChange={(permissions) => setEditForm({ ...editForm, permissions })}
            />
          </div>

          {/* Rate limit */}
          <div>
            <label htmlFor="edit-rate-limit" className="label">
              Rate Limit (req/min)
            </label>
            <input
              id="edit-rate-limit"
              type="number"
              min={1}
              max={10000}
              className="input"
              value={editForm.rate_limit}
              onChange={(e) =>
                setEditForm({ ...editForm, rate_limit: Number(e.target.value) })
              }
            />
          </div>

          {/* Active toggle */}
          <div>
            <label className="label mb-1">Estado</label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                role="switch"
                aria-checked={editForm.active}
                onClick={() => setEditForm({ ...editForm, active: !editForm.active })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-vida-500 focus:ring-offset-2 ${
                  editForm.active ? 'bg-vida-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    editForm.active ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-700">
                {editForm.active ? 'Activa' : 'Inactiva'}
              </span>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowEditModal(false);
                setSelectedKey(null);
              }}
              disabled={editLoading}
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={editLoading}>
              {editLoading ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ------------------------------------------------------------------ */}
      {/* Delete confirmation                                                 */}
      {/* ------------------------------------------------------------------ */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setSelectedKey(null);
        }}
        onConfirm={handleDelete}
        title="Eliminar API Key"
        message={`¿Estás seguro de que quieres eliminar la key "${selectedKey?.name}"? Todas las integraciones que la usen dejarán de funcionar inmediatamente.`}
        confirmText="Eliminar"
        variant="danger"
        loading={deleteLoading}
      />

      {/* ------------------------------------------------------------------ */}
      {/* Reveal key dialog (shown once after creation)                       */}
      {/* ------------------------------------------------------------------ */}
      <RevealKeyDialog
        open={showRevealDialog}
        apiKey={revealedKey}
        onClose={() => {
          setShowRevealDialog(false);
          setRevealedKey('');
        }}
      />
    </div>
  );
}
